// Public profile page at /$username.
//
// Fetches GET /users/:handle for the public profile data.
// Shows an "Edit profile" inline form when the viewer is looking at their own profile.
// Edit form patches /me with handle, avatarUrl, bio, and socialLinks changes.
//
// Note on routing: TanStack Router doesn't allow a literal @ in a segment, so
// links to this route use /${handle} and the nav @handle chip links here too.

import { createFileRoute } from '@tanstack/react-router';
import {
  Cloud,
  Download,
  Globe,
  Instagram,
  Lock,
  type LucideIcon,
  Music,
  Music2,
  Pin,
  PinOff,
  UserCheck,
  UserPlus,
  Youtube,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '~/lib/api-client';
import { useSession } from '~/lib/auth';
import {
  type ConnState,
  connectionStatus,
  removeConnection,
  respondConnection,
  sendConnection,
} from '~/lib/connections';
import { SITE_URL, buildHead } from '~/seo/site';
import { ProducerHandle, SupporterAvatar } from '~/ui/ProducerHandle';
import { ReportButton } from '~/ui/ReportButton';
import { Badge } from '~/ui/components/badge';
import { Button } from '~/ui/components/button';
import { Card, CardContent, CardHeader } from '~/ui/components/card';

export const Route = createFileRoute('/$username')({
  head: ({ params }) => {
    const handle = params.username.replace(/^@+/, '');
    const head = buildHead({
      title: `@${handle}`,
      description: `Producer profile for @${handle} on Prod Battle. Tracks, wins, ranks per genre, and battle submissions.`,
      path: `/${params.username}`,
      type: 'profile',
    });
    const personSchema = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: `@${handle}`,
      url: `${SITE_URL}/${params.username}`,
      sameAs: [],
    };
    return {
      ...head,
      meta: [...head.meta, { 'script:ld+json': personSchema }],
    };
  },
  component: Profile,
});

type PublicSubmission = {
  id: string;
  title: string | null;
  audioUrl: string;
  score: number;
  rank: number | null;
  mode: string;
  createdAt: string;
};

type PinnedTrack = {
  id: string;
  title: string | null;
  audioUrl: string;
  waveformUrl: string | null;
  score: number;
  finalRank: number | null;
  producerHandle: string;
  isSupporter: boolean;
  genreSlug: string;
  createdAt: string;
};

type SocialLinks = {
  spotify?: string;
  soundcloud?: string;
  bandcamp?: string;
  instagram?: string;
  youtube?: string;
  website?: string;
};

type RankedTier = {
  genreSlug: string;
  genreName: string;
  lp: number;
  tier: string;
  wins: number;
  losses: number;
};

type PublicProfile = {
  id: string;
  handle: string;
  avatarUrl: string | null;
  role: 'producer' | 'ar' | 'admin';
  bio: string | null;
  socialLinks: Record<string, string>;
  createdAt: string;
  honorStars: number;
  ownHonor: number | null;
  calibrating: boolean;
  rankedTiers: RankedTier[];
  stats: {
    wins: number;
    matches: number;
    streakDays: number;
  };
  recentSubmissions: PublicSubmission[];
  // Supporter perks (always present in API response).
  isSupporter: boolean;
  accentColor: string | null;
  pinnedTracks: PinnedTrack[];
  // Accepted friends (empty when the owner hides the section).
  friends?: { handle: string; avatarUrl: string | null }[];
};

function formatJoined(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 text-center">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  if (role === 'admin') return <Badge variant="outline">Admin</Badge>;
  if (role === 'ar') return <Badge variant="secondary">A&R</Badge>;
  return null;
}

// Friend request / connection button. Self-contained (the profile page doesn't
// use react-query): loads the current state, then sends/accepts/removes.
function AddFriendButton({ userId }: { userId: string }) {
  const [state, setState] = useState<ConnState | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    connectionStatus(userId)
      .then((r) => alive && setState(r.state))
      .catch(() => alive && setState('none'));
    return () => {
      alive = false;
    };
  }, [userId]);
  if (state === null) return null;
  const act = async (fn: () => Promise<unknown>, next: ConnState) => {
    setBusy(true);
    try {
      await fn();
      setState(next);
    } catch {
      /* ignore */
    }
    setBusy(false);
  };
  if (state === 'friends')
    return (
      <Button
        variant="outline"
        disabled={busy}
        onClick={() => act(() => removeConnection(userId), 'none')}
      >
        <UserCheck className="size-4" /> Friends
      </Button>
    );
  if (state === 'outgoing')
    return (
      <Button
        variant="outline"
        disabled={busy}
        onClick={() => act(() => removeConnection(userId), 'none')}
      >
        Requested
      </Button>
    );
  if (state === 'incoming')
    return (
      <div className="flex gap-2">
        <Button
          disabled={busy}
          onClick={() => act(() => respondConnection(userId, true), 'friends')}
        >
          Accept
        </Button>
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => act(() => respondConnection(userId, false), 'none')}
        >
          Decline
        </Button>
      </div>
    );
  return (
    <Button disabled={busy} onClick={() => act(() => sendConnection(userId), 'outgoing')}>
      <UserPlus className="size-4" /> Add friend
    </Button>
  );
}

// Social link icons and labels used in both the view and edit sections.
const SOCIAL_FIELDS: Array<{
  key: keyof SocialLinks;
  label: string;
  placeholder: string;
  Icon: LucideIcon;
}> = [
  {
    key: 'spotify',
    label: 'Spotify',
    placeholder: 'https://open.spotify.com/artist/...',
    Icon: Music,
  },
  {
    key: 'soundcloud',
    label: 'SoundCloud',
    placeholder: 'https://soundcloud.com/...',
    Icon: Cloud,
  },
  {
    key: 'bandcamp',
    label: 'Bandcamp',
    placeholder: 'https://yourname.bandcamp.com',
    Icon: Music2,
  },
  {
    key: 'instagram',
    label: 'Instagram',
    placeholder: 'https://instagram.com/...',
    Icon: Instagram,
  },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/...', Icon: Youtube },
  { key: 'website', label: 'Website', placeholder: 'https://yoursite.com', Icon: Globe },
];

const MAX_AVATAR_BYTES = 2 * 1024 * 1024; // 2 MB
const ACCEPTED_AVATAR_TYPES = ['image/jpeg', 'image/png'] as const;
type AvatarContentType = (typeof ACCEPTED_AVATAR_TYPES)[number];

function EditProfileForm({
  profile,
  onSaved,
  onCancel,
}: {
  profile: PublicProfile;
  onSaved: (updated: {
    handle: string;
    avatarUrl: string | null;
    bio: string | null;
    socialLinks: Record<string, string>;
    accentColor: string | null;
  }) => void;
  onCancel: () => void;
}) {
  const [handle, setHandle] = useState(profile.handle);
  // avatarUrl holds the final URL to PATCH /me. We start with the current signed URL but
  // after a successful upload we swap it for the unsigned publicUrl.
  const [avatarUrl, setAvatarUrl] = useState<string | null>(profile.avatarUrl);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(profile.avatarUrl);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const [bio, setBio] = useState(profile.bio ?? '');
  const [accentColor, setAccentColor] = useState<string>(profile.accentColor ?? '#a855f7');
  const [socialLinks, setSocialLinks] = useState<SocialLinks>({
    spotify: profile.socialLinks.spotify ?? '',
    soundcloud: profile.socialLinks.soundcloud ?? '',
    bandcamp: profile.socialLinks.bandcamp ?? '',
    instagram: profile.socialLinks.instagram ?? '',
    youtube: profile.socialLinks.youtube ?? '',
    website: profile.socialLinks.website ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError(null);

    if (!ACCEPTED_AVATAR_TYPES.includes(file.type as AvatarContentType)) {
      setAvatarError('Only JPG and PNG files are supported.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setAvatarError('Image must be 2 MB or smaller.');
      e.target.value = '';
      return;
    }

    setAvatarUploading(true);
    try {
      // Step 1: get a presigned PUT URL from the API.
      const { uploadUrl, publicUrl } = await api<{
        uploadUrl: string;
        publicUrl: string;
        key: string;
        maxBytes: number;
      }>('/me/avatar/upload-url', {
        method: 'POST',
        body: JSON.stringify({ contentType: file.type }),
      });

      // Step 2: PUT the file directly to S3 - no credentials cookie needed here.
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putRes.ok) throw new Error(`Upload failed: ${putRes.status}`);

      // Step 3: store the public (unsigned) URL; PATCH /me will write it to the DB.
      setAvatarUrl(publicUrl);
      // Show a local object URL as preview so the user sees the new image immediately.
      setAvatarPreview(URL.createObjectURL(file));
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : 'Upload failed. Try again.');
    } finally {
      setAvatarUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // biome-ignore lint/suspicious/noExplicitAny: intentional dynamic patch body
      const body: Record<string, any> = {};
      if (handle !== profile.handle) body.handle = handle;
      if (avatarUrl !== profile.avatarUrl) body.avatarUrl = avatarUrl;
      const bioTrimmed = bio.trim() || null;
      if (bioTrimmed !== profile.bio) body.bio = bioTrimmed;

      // Build socialLinks object - omit empty values.
      const links: Record<string, string> = {};
      for (const { key } of SOCIAL_FIELDS) {
        const val = (socialLinks[key] ?? '').trim();
        if (val) links[key] = val;
      }
      // Always include if links differ from profile.
      const profileLinks = profile.socialLinks ?? {};
      const linksChanged = JSON.stringify(links) !== JSON.stringify(profileLinks);
      if (linksChanged) body.socialLinks = links;

      // Accent color: paid users only - include when changed.
      if (profile.isSupporter && accentColor !== (profile.accentColor ?? '#a855f7')) {
        body.accentColor = accentColor;
      }

      if (Object.keys(body).length > 0) {
        await api('/me', {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      }
      onSaved({
        handle: (body.handle as string | undefined) ?? profile.handle,
        avatarUrl: 'avatarUrl' in body ? (body.avatarUrl as string | null) : profile.avatarUrl,
        bio: 'bio' in body ? (body.bio as string | null) : profile.bio,
        socialLinks:
          'socialLinks' in body ? (body.socialLinks as Record<string, string>) : profileLinks,
        accentColor:
          'accentColor' in body ? (body.accentColor as string | null) : profile.accentColor,
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError('That handle is already taken.');
      } else {
        setError(e instanceof ApiError ? e.message : 'Failed to save. Try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-primary/40 bg-primary/5">
      <CardHeader>
        <h3 className="text-lg font-semibold">Edit profile</h3>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Handle */}
        <div className="space-y-1">
          <label htmlFor="edit-handle" className="text-sm font-medium">
            Handle
          </label>
          <input
            id="edit-handle"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="your-handle"
            maxLength={20}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="text-xs text-muted-foreground">3-20 chars, letters, digits, _ or -</p>
        </div>

        {/* Avatar upload */}
        <div className="space-y-2">
          <span className="text-sm font-medium">Avatar</span>
          <div className="flex items-center gap-4">
            {avatarPreview ? (
              // biome-ignore lint/a11y/useAltText: decorative avatar preview
              <img
                src={avatarPreview}
                className="size-16 rounded-full object-cover border border-border"
              />
            ) : (
              <div className="size-16 rounded-full bg-muted flex items-center justify-center text-xl font-bold text-muted-foreground">
                {handle.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <input
                id="edit-avatar-file"
                type="file"
                accept="image/jpeg,image/png"
                onChange={handleFileChange}
                disabled={avatarUploading}
                className="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
              />
              {avatarUrl && (
                <button
                  type="button"
                  onClick={() => {
                    setAvatarUrl(null);
                    setAvatarPreview(null);
                  }}
                  className="text-xs text-muted-foreground hover:text-destructive w-fit"
                >
                  Clear avatar
                </button>
              )}
              {avatarUploading && <p className="text-xs text-muted-foreground">Uploading...</p>}
            </div>
          </div>
          {avatarError && <p className="text-sm text-destructive">{avatarError}</p>}
          <p className="text-xs text-muted-foreground">JPG or PNG, max 2 MB.</p>
        </div>

        {/* Bio */}
        <div className="space-y-1">
          <label htmlFor="edit-bio" className="text-sm font-medium">
            Bio
          </label>
          <textarea
            id="edit-bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="A few words about you..."
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
          <p className="text-xs text-muted-foreground text-right">{bio.length}/500</p>
        </div>

        {/* Accent color - supporter only */}
        {profile.isSupporter && (
          <div className="space-y-1">
            <label htmlFor="edit-accent-color" className="text-sm font-medium">
              Accent color
              <span className="ml-2 text-xs font-normal text-yellow-400">Supporter</span>
            </label>
            <div className="flex items-center gap-3">
              <input
                id="edit-accent-color"
                type="color"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                className="size-9 cursor-pointer rounded border border-border bg-transparent p-0.5"
              />
              <input
                type="text"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                maxLength={7}
                placeholder="#a855f7"
                className="w-28 rounded-md border border-border bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Colors your avatar ring. Enter a hex value like #a855f7.
            </p>
          </div>
        )}

        {/* Producer links */}
        <div className="space-y-2">
          <span className="text-sm font-medium">Producer links</span>
          <div className="space-y-2">
            {SOCIAL_FIELDS.map(({ key, label, placeholder, Icon }) => (
              <div key={key} className="flex items-center gap-2">
                <Icon size={16} className="shrink-0 text-muted-foreground" />
                <label
                  htmlFor={`edit-social-${key}`}
                  className="w-24 text-sm text-muted-foreground shrink-0"
                >
                  {label}
                </label>
                <input
                  id={`edit-social-${key}`}
                  type="url"
                  value={socialLinks[key] ?? ''}
                  onChange={(e) => setSocialLinks((prev) => ({ ...prev, [key]: e.target.value }))}
                  placeholder={placeholder}
                  className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving || avatarUploading}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SocialLinksRow({ links }: { links: Record<string, string> }) {
  const entries = SOCIAL_FIELDS.filter(({ key }) => links[key]);
  if (entries.length === 0) return null;
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {entries.map(({ key, label, Icon }) => (
        <a
          key={key}
          href={links[key]}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon size={18} />
        </a>
      ))}
    </div>
  );
}

type EarnedAchievement = {
  key: string;
  title: string;
  description: string;
  category: 'streak' | 'milestone' | 'tier' | 'creator' | 'community' | 'mode';
  earnedAt: string;
  hiddenByUser: boolean;
};

function AchievementsStrip({
  username,
  isOwn,
}: {
  username: string;
  isOwn: boolean;
}) {
  const [items, setItems] = useState<EarnedAchievement[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const url = isOwn ? '/me/achievements' : `/users/${encodeURIComponent(username)}/achievements`;
    api<{ items: EarnedAchievement[] }>(url)
      .then((r) => setItems(r.items))
      .catch((e) => setErr((e as Error).message));
  }, [username, isOwn]);

  if (err) return null; // silent - no achievements section vs error noise
  if (!items) return null;
  if (items.length === 0) {
    return (
      <div className="space-y-2">
        <h3 className="text-xl font-semibold">Achievements</h3>
        <p className="text-sm text-muted-foreground">No achievements yet.</p>
      </div>
    );
  }

  async function toggle(key: string, hidden: boolean) {
    if (!items) return;
    const next = items.map((a) => (a.key === key ? { ...a, hiddenByUser: hidden } : a));
    setItems(next);
    try {
      await api(`/me/achievements/${encodeURIComponent(key)}`, {
        method: 'PATCH',
        body: JSON.stringify({ hidden }),
      });
    } catch (e) {
      setErr((e as Error).message);
      // Revert on error.
      setItems(items);
    }
  }

  return (
    <div className="space-y-2">
      <h3 className="text-xl font-semibold">
        Achievements
        <span className="ml-2 text-base font-normal text-muted-foreground">
          ({items.filter((a) => !a.hiddenByUser).length}
          {isOwn && items.some((a) => a.hiddenByUser) ? ` shown / ${items.length} earned` : ''})
        </span>
      </h3>
      <div className="flex flex-wrap gap-2">
        {items.map((a) => (
          <div
            key={a.key}
            className={`group relative rounded-md border px-3 py-2 text-xs ${
              a.hiddenByUser
                ? 'border-dashed border-border/50 bg-muted/30 text-muted-foreground'
                : 'border-border bg-card'
            }`}
            title={a.description}
          >
            <span className="font-medium">{a.title}</span>
            {isOwn && (
              <button
                type="button"
                onClick={() => toggle(a.key, !a.hiddenByUser)}
                className="ml-2 text-[0.65rem] underline opacity-60 hover:opacity-100"
                aria-label={a.hiddenByUser ? 'Show on profile' : 'Hide from profile'}
              >
                {a.hiddenByUser ? 'show' : 'hide'}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SubmissionCard({
  sub,
  isOwn,
  isOwnerSupporter,
  isPinned,
  onTogglePin,
}: {
  sub: PublicSubmission;
  isOwn: boolean;
  isOwnerSupporter: boolean;
  isPinned?: boolean;
  onTogglePin?: (id: string, pinned: boolean) => void;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // GET /me/submissions/:id/download returns 302 to signed URL.
      // We navigate to it directly so the browser's download mechanism kicks in.
      const res = await fetch(`/api/me/submissions/${sub.id}/download`, {
        credentials: 'include',
        redirect: 'manual',
      });
      const location = res.headers.get('Location') ?? (res as Response & { url: string }).url;
      if (location) {
        const a = document.createElement('a');
        a.href = location;
        a.download = sub.title ? `${sub.title}.opus` : `submission-${sub.id}.opus`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch {
      // Silent failure - user can try again.
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          {sub.rank != null && (
            <span className="text-xs font-mono text-muted-foreground">#{sub.rank}</span>
          )}
          <span className="font-semibold">{sub.title ?? 'Untitled'}</span>
          <Badge variant="secondary" className="capitalize">
            {sub.mode}
          </Badge>
          {sub.rank === 1 && <Badge>winner</Badge>}
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {sub.score.toFixed(1)} pts
          </span>
          {/* Pin toggle - only for owner, only when they're a supporter */}
          {isOwn && isOwnerSupporter && onTogglePin && (
            <button
              type="button"
              onClick={() => onTogglePin(sub.id, !isPinned)}
              title={isPinned ? 'Unpin track' : 'Pin track (up to 3)'}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {isPinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
            </button>
          )}
          {/* Download - own supporter tracks only */}
          {isOwn && isOwnerSupporter && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              title="Download original file"
              className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              <Download className="size-4" />
            </button>
          )}
          {/* Lock icon for non-supporters viewing their own submissions */}
          {isOwn && !isOwnerSupporter && (
            <a
              href="/pricing"
              title="Upgrade to supporter to download"
              className="text-muted-foreground/50 hover:text-muted-foreground transition-colors"
            >
              <Lock className="size-4" />
            </a>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {new Date(sub.createdAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
      </CardHeader>
      <CardContent>
        {/* biome-ignore lint/a11y/useMediaCaption: user-submitted audio */}
        <audio ref={audioRef} src={sub.audioUrl} controls className="w-full" preload="none" />
      </CardContent>
    </Card>
  );
}

// Pinned tracks section - shown on all profiles where pinnedTracks.length > 0.
function PinnedTracksSection({ tracks }: { tracks: PinnedTrack[] }) {
  if (tracks.length === 0) return null;
  const audioRef = useRef<HTMLAudioElement | null>(null);

  return (
    <div className="space-y-3">
      <h3 className="text-xl font-semibold flex items-center gap-2">
        <Pin className="size-4 text-primary" aria-hidden />
        Pinned
      </h3>
      <div className="space-y-3">
        {tracks.map((t) => (
          <Card key={t.id} className="border-primary/30 bg-primary/5">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2 flex-wrap">
                {t.finalRank != null && (
                  <span className="text-xs font-mono text-muted-foreground">#{t.finalRank}</span>
                )}
                <span className="font-semibold">{t.title ?? 'Untitled'}</span>
                <Badge variant="secondary" className="capitalize">
                  {t.genreSlug}
                </Badge>
                <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                  {t.score.toFixed(1)} pts
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {new Date(t.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </CardHeader>
            <CardContent>
              {/* biome-ignore lint/a11y/useMediaCaption: user-submitted audio */}
              <audio ref={audioRef} src={t.audioUrl} controls className="w-full" preload="none" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Profile() {
  const { username } = Route.useParams();
  const { data: session } = useSession();

  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  // Pinned track IDs - synced with the API when owner toggles.
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api<PublicProfile>(`/users/${username}`)
      .then((p) => {
        setProfile(p);
        setPinnedIds(p.pinnedTracks.map((t) => t.id));
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) {
          setNotFound(true);
        }
      })
      .finally(() => setLoading(false));
  }, [username]);

  // Toggle a track's pinned state. The API enforces the max-3 / supporter gates.
  const handleTogglePin = async (trackId: string, shouldPin: boolean) => {
    if (!profile) return;
    const next = shouldPin
      ? [...new Set([...pinnedIds, trackId])].slice(0, 3)
      : pinnedIds.filter((id) => id !== trackId);
    // Optimistic update.
    setPinnedIds(next);
    try {
      await api('/me/pinned-tracks', {
        method: 'PUT',
        body: JSON.stringify({ submissionIds: next }),
      });
    } catch {
      // Revert on failure.
      setPinnedIds(pinnedIds);
    }
  };

  // After a successful PATCH /me the handle may have changed. If so, redirect
  // to the new URL. Otherwise just update local state.
  const handleSaved = (updated: {
    handle: string;
    avatarUrl: string | null;
    bio: string | null;
    socialLinks: Record<string, string>;
    accentColor: string | null;
  }) => {
    setEditing(false);
    if (profile) {
      const newProfile: PublicProfile = {
        ...profile,
        handle: updated.handle,
        avatarUrl: updated.avatarUrl,
        bio: updated.bio,
        socialLinks: updated.socialLinks,
        accentColor: updated.accentColor,
      };
      setProfile(newProfile);
      // If the handle changed, navigate to the new URL without a full reload.
      if (updated.handle !== username) {
        window.location.assign(`/${updated.handle}`);
      }
    }
  };

  const isOwn = session?.handle === username;

  if (loading) {
    return (
      <section className="mx-auto max-w-4xl px-4 py-12">
        <p className="text-muted-foreground">Loading...</p>
      </section>
    );
  }

  if (notFound || !profile) {
    return (
      <section className="mx-auto max-w-4xl px-4 py-12 space-y-4">
        <h2 className="text-2xl font-bold">Profile not found</h2>
        <p className="text-muted-foreground">
          No active user found at <code className="font-mono">/{username}</code>.
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-4xl px-4 py-12 space-y-8">
      <header className="flex items-start gap-6">
        <SupporterAvatar
          src={profile.avatarUrl}
          handle={profile.handle}
          isSupporter={profile.isSupporter}
          accentColor={profile.accentColor}
          size="lg"
        />
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-3xl font-bold">
              <ProducerHandle handle={profile.handle} isSupporter={profile.isSupporter} />
            </h2>
            <RoleBadge role={profile.role} />
          </div>
          {profile.bio && (
            <p className="text-foreground text-base leading-relaxed">{profile.bio}</p>
          )}
          <SocialLinksRow links={profile.socialLinks ?? {}} />
          <div className="flex items-center gap-3 pt-1 text-xs text-muted-foreground">
            <span>Producer since {formatJoined(profile.createdAt)}</span>
            <span aria-label={`Honor: ${profile.honorStars} of 5 stars`}>
              <span className="text-yellow-500">{'★'.repeat(profile.honorStars)}</span>
              <span className="opacity-30">{'★'.repeat(5 - profile.honorStars)}</span>
              {profile.ownHonor != null && (
                <span className="ml-2 font-mono text-[11px] text-foreground/80">
                  {profile.ownHonor}/100
                </span>
              )}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isOwn && !editing && (
            <Button variant="outline" onClick={() => setEditing(true)}>
              Edit profile
            </Button>
          )}
          {!isOwn && session && <AddFriendButton userId={profile.id} />}
          {!isOwn && session && (
            <ReportButton tone="icon" subjectType="profile" subjectId={profile.id} />
          )}
        </div>
      </header>

      {editing && isOwn && (
        <EditProfileForm
          profile={profile}
          onSaved={handleSaved}
          onCancel={() => setEditing(false)}
        />
      )}

      {/* Stats grid - matches, wins, win rate, streak. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Matches" value={profile.stats.matches} />
        <StatCard label="Wins" value={profile.stats.wins} />
        <StatCard
          label="Win rate"
          value={
            profile.stats.matches > 0
              ? `${Math.round((profile.stats.wins / profile.stats.matches) * 100)}%`
              : '-'
          }
        />
        <StatCard label="Streak (days)" value={profile.stats.streakDays} />
      </div>

      {/* Ranked tiers per genre - hidden during calibration. */}
      {profile.calibrating ? (
        <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
          Calibrating - tier appears after the first 10 ranked matches.
        </div>
      ) : profile.rankedTiers.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-xl font-semibold">Ranked</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {profile.rankedTiers.map((t) => (
              <div
                key={t.genreSlug}
                className="rounded-md border border-border bg-card px-4 py-3 flex items-center justify-between"
              >
                <div>
                  <div className="text-sm font-medium">{t.genreName}</div>
                  <div className="text-xs text-muted-foreground">
                    {t.wins}W - {t.losses}L
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold">{t.tier}</div>
                  <div className="text-xs text-muted-foreground">{t.lp} LP</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <AchievementsStrip username={username} isOwn={isOwn} />

      {/* Pinned tracks - visible to all, with toggle for owner supporters. */}
      {(profile.pinnedTracks.length > 0 || (isOwn && profile.isSupporter)) && (
        <PinnedTracksSection tracks={profile.pinnedTracks} />
      )}

      <div className="space-y-4">
        <h3 className="text-xl font-semibold">
          Recent submissions
          {profile.recentSubmissions.length > 0 && (
            <span className="ml-2 text-base font-normal text-muted-foreground">
              ({profile.recentSubmissions.length})
            </span>
          )}
        </h3>
        {profile.recentSubmissions.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground">
            No public submissions yet.
          </div>
        ) : (
          <div className="space-y-4">
            {profile.recentSubmissions.map((sub) => (
              <SubmissionCard
                key={sub.id}
                sub={sub}
                isOwn={isOwn}
                isOwnerSupporter={profile.isSupporter}
                isPinned={pinnedIds.includes(sub.id)}
                onTogglePin={isOwn ? handleTogglePin : undefined}
              />
            ))}
          </div>
        )}
      </div>

      {profile.friends && profile.friends.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold">Friends ({profile.friends.length})</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {profile.friends.map((f) => (
              <a
                key={f.handle}
                href={`/${f.handle}`}
                className="flex items-center gap-2 rounded-lg border border-border px-2 py-1.5 hover:border-primary"
              >
                {f.avatarUrl ? (
                  <img src={f.avatarUrl} alt="" className="size-6 rounded-full object-cover" />
                ) : (
                  <span className="flex size-6 items-center justify-center rounded-full bg-muted text-xs">
                    {f.handle[0]?.toUpperCase()}
                  </span>
                )}
                <span className="text-sm">@{f.handle}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
